import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationTriangle,
  faTimes,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import styles from './CharacterStatusNotification.module.css';

interface CharacterStatusAnalysis {
  characterId: string;
  characterName: string;
  hasPrompt: boolean;
  hasImage: boolean;
  promptId?: string;
  imageUrl?: string;
  scenePromptsUsingCharacter: string[];
  scenePromptTitles: string[];
}

interface CharacterStatusNotificationProps {
  isVisible: boolean;
  onDismiss: () => void;
  characterStatusAnalysis: CharacterStatusAnalysis[];
}

const CharacterStatusNotification: React.FC<CharacterStatusNotificationProps> = ({
  isVisible,
  onDismiss,
  characterStatusAnalysis
}) => {
  if (!isVisible || !characterStatusAnalysis?.length) return null;

  // Calculate statistics
  const totalCharacters = characterStatusAnalysis.length;
  const charactersNeedingPrompts = characterStatusAnalysis.filter(char => !char.hasPrompt);
  const charactersNeedingImages = characterStatusAnalysis.filter(char => char.hasPrompt && !char.hasImage);
  
  // If all characters are complete, show success state
  const allComplete = charactersNeedingPrompts.length === 0 && charactersNeedingImages.length === 0;

  // Identify next action item (prioritize prompts first, then images)
  let nextActionMessage = '';
  if (charactersNeedingPrompts.length > 0) {
    const firstCharacterNeedingPrompt = charactersNeedingPrompts[0];
    nextActionMessage = `Next: Generate prompt for "${firstCharacterNeedingPrompt.characterName}" (${charactersNeedingPrompts.length}/${totalCharacters} prompts needed)`;
  } else if (charactersNeedingImages.length > 0) {
    const firstCharacterNeedingImage = charactersNeedingImages[0];
    nextActionMessage = `Next: Generate image for "${firstCharacterNeedingImage.characterName}" (${charactersNeedingImages.length}/${totalCharacters} images needed)`;
  }

  return (
    <div 
      className={styles.futuristicContainer}
      style={{
        background: allComplete 
          ? 'linear-gradient(135deg, #0f766e 0%, #059669 50%, #10b981 100%)' 
          : 'linear-gradient(135deg, #ea580c 0%, #dc2626 50%, #b91c1c 100%)',
        boxShadow: `
          0 4px 20px rgba(0, 0, 0, 0.25),
          inset 0 1px 0 rgba(255, 255, 255, 0.1),
          0 0 30px rgba(${allComplete ? '16, 185, 129' : '239, 68, 68'}, 0.3)
        `
      }}
    >
      {/* Animated background effect */}
      <div 
        className={styles.shimmerEffect}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              90deg,
              transparent 0%,
              rgba(255, 255, 255, 0.05) 25%,
              rgba(255, 255, 255, 0.1) 50%,
              rgba(255, 255, 255, 0.05) 75%,
              transparent 100%
            )
          `,
          transform: 'translateX(-100%)'
        }} 
      />
      
      <div className={styles.contentWrapper}>
        <div className={styles.iconContainer}>
          <FontAwesomeIcon 
            icon={allComplete ? faCheckCircle : faExclamationTriangle} 
            style={{ 
              fontSize: '12px',
              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
            }}
          />
        </div>
        <span className={styles.textContent}>
          {allComplete ? (
            `Character consistency mode: ✅ Setup complete! All ${totalCharacters} characters are ready.`
          ) : (
            `Character consistency mode: ${nextActionMessage}`
          )}
        </span>
      </div>
      
      <button
        onClick={onDismiss}
        className={styles.dismissButton}
        title="Dismiss notification"
      >
        <FontAwesomeIcon 
          icon={faTimes} 
          style={{ 
            fontSize: '12px',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
          }} 
        />
      </button>
    </div>
  );
};

export default CharacterStatusNotification;
